// components/ChatActionButton.tsx
import React, { useCallback, useMemo } from 'react';
import { Send, ArrowUp } from '@/components/ui/lucide-memo';
import { ChatStateItem } from  '#types/types';
import { Tooltip, TooltipContent, TooltipKey, TooltipTrigger } from './ui/tooltip'; // Import your tooltip components
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import StopButton, { STOP_BUTTON_SIZE_COMPOSER, STOP_BUTTON_SIZE_PILL } from '@/components/portal/StopButton';
import { agentIsWorking } from '#lib/chat/agentIsWorking';
import { useSlidesStore } from './slides/slidesStore';
import { readCampaignRow } from '#lib/leasing/campaignRow';
import { ACTIVE_SOURCING_CAMPAIGN_STATUSES } from '#lib/leasing/sourcingCampaignLifecycle';

interface ChatActionButtonProps {
  chatState: ChatStateItem[];
  isRunningScript: boolean;
  isStopRequestedByUI: boolean;
  canSendMessage: boolean;
  onSend: () => void;
  isQueuingMessage: boolean;
  /** ChatGPT-style: render as a circular send/stop button, white, or 'blue' as in ChatGPT's work
   *  composer (the Source Launch bar's arrow, so Source's chat sends with the same mark). */
  pill?: boolean | 'blue';
}

const COMMON_BUTTON_CLASSES = 'p-2 rounded-lg transition-colors duration-150 flex-shrink-0';
const ICON_SIZE = 14;

const ChatActionButtonComponent: React.FC<ChatActionButtonProps> = ({
  chatState,
  isRunningScript,
  isStopRequestedByUI,
  canSendMessage,
  onSend,
  isQueuingMessage,
  pill,
}) => {
  const { kernel } = useKernel();
  // A running bulk enrichment IS agent activity: same stop affordance. The stop click
  // raises sourceEnrichStopRequested; the bulk loop pauses between brands (resumable).
  const enrichActive = useKernelStore((s) => (s.sourceEnrichInFlight ?? 0) > 0);
  const enrichStopRequested = useKernelStore((s) => Boolean(s.sourceEnrichStopRequested));
  const activeSessionId = useKernelStore(s => s.activeChatSessionId);
  const proposalActive = useSlidesStore(s => !!activeSessionId && !!s.getProposalRun(activeSessionId));
  const pillBase = 'flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition-colors duration-150';
  const sendOrQueueDisabled = !canSendMessage || isQueuingMessage;
  const liveTelemetry = agentIsWorking(chatState);
  // The open run's research and scoring are the agent working too: the stop stays on the composer
  // for the whole run, not only while a sentence streams (owner, 2026-09-28).
  const runIsLive = useKernelStore((s) => {
    const campaign = readCampaignRow(s.inventory, s.sourceSelectedCampaignId);
    return Boolean(s.sourceSelectedCampaignId) && (ACTIVE_SOURCING_CAMPAIGN_STATUSES as readonly string[]).includes(String(campaign?.status ?? '').toUpperCase());
  });
  const aiIsActive = isRunningScript || liveTelemetry || enrichActive || proposalActive || runIsLive;
  const stopRequested = isStopRequestedByUI || (enrichActive && enrichStopRequested && !isRunningScript && !liveTelemetry && !proposalActive);

  const onStop = useCallback(() => {
    const k = kernel.current;
    if (!k) return;
    const sessionId = k.getActiveChatSessionId();
    // Said the instant it is pressed: an abort can take seconds to land, and silence reads as broken.
    if (sessionId) k.setSessionStatus(sessionId, 'Stopping…');
    if ((k.getState().sourceEnrichInFlight ?? 0) > 0) {
      k.setState({ sourceEnrichStopRequested: true });
    }
    // A sourcing run has no stream to abort: its progress line is what keeps this button up, so
    // aborting alone left "Stopping assistant..." on screen until the pass ended, minutes later
    // (app.fater.ai, 2026-09-26). Stop the run on screen too; it keeps every brand already scored.
    const campaignId = k.getState().sourceSelectedCampaignId;
    const campaign = readCampaignRow(k.getState().inventory, campaignId);
    // Research runs on the worker as part of the run, so stopping the run stops the research too.
    const runIsLive = Boolean(campaignId) && (ACTIVE_SOURCING_CAMPAIGN_STATUSES as readonly string[]).includes(String(campaign?.status ?? '').toUpperCase());
    if (campaignId && runIsLive) void k.stopSourcingCampaign(campaignId);
    const runtime = sessionId ? k.readEntityRuntimeState('ChatSession', sessionId) : null;
    const somethingWillSettle = runIsLive || Boolean(runtime?.abortController) || Boolean(runtime?.runningScriptId);
    // A stop ends the session's sourcing mandate too: nothing the agent drives may start again on it.
    if (sessionId && k.getState().sourceMandates?.[sessionId]) {
      const { [sessionId]: _stopped, ...rest } = k.getState().sourceMandates;
      k.setState({ sourceMandates: rest });
    }
    k.requestScriptStop();
    // Nothing left to wind down: the stale live line is the only thing holding the button up.
    if (sessionId && !somethingWillSettle) k.setSessionStatus(sessionId, '');
  }, [kernel]);

  const sendMessageTooltip = useMemo(() => {
    if (aiIsActive) return ""; // Stop button will have its own tooltip
    if (isQueuingMessage) return "Sending…";
    if (!canSendMessage) return "Type a message";
    return "Send";
  }, [aiIsActive, isQueuingMessage, canSendMessage]);

  const stopButtonTooltip = useMemo(() => {
    if (!aiIsActive) return "";
    if (enrichActive && !isRunningScript && !liveTelemetry) {
      return enrichStopRequested ? "Pausing…" : "Pause enrichment";
    }
    if (isStopRequestedByUI) return "Stopping…";
    if (runIsLive && !isRunningScript && !liveTelemetry) return "Stop the run";
    return "Stop";
  }, [aiIsActive, isStopRequestedByUI, isRunningScript, enrichActive, enrichStopRequested, liveTelemetry, runIsLive]);

  const actionButton = useMemo(() => {
    if (aiIsActive) {
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            {/* Stop-already-requested keeps its old semantics: the click is dropped and the button
                is disabled. It just says so with the neighbours' dimmed disabled state now, rather
                than a spinner, so the square stays the only glyph this control ever shows. */}
            <StopButton
              onClick={!stopRequested ? onStop : undefined}
              disabled={stopRequested}
              size={pill ? STOP_BUTTON_SIZE_PILL : STOP_BUTTON_SIZE_COMPOSER}
              round={Boolean(pill)}
              // The Radix tooltip below already names what is being stopped; a native title on top
              // of it would open a second, poorer tooltip over the same button.
              title={undefined}
            />
          </TooltipTrigger>
          <TooltipContent side="top" sideOffset={5}>{stopButtonTooltip}</TooltipContent>
        </Tooltip>
      );
    }

    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={onSend}
            disabled={sendOrQueueDisabled}
            data-chat-send-button
            className={pill === 'blue'
              ? `${pillBase} ${sendOrQueueDisabled ? 'bg-[#3862C2]/40 text-white cursor-not-allowed' : 'bg-[#3862C2] text-white hover:bg-[#4270D6]'}`
              : pill
              ? `${pillBase} ${sendOrQueueDisabled ? 'bg-white/60 text-black cursor-not-allowed' : 'bg-white text-black hover:bg-[#ECECEC]'}`
              : `${COMMON_BUTTON_CLASSES} ${
                  sendOrQueueDisabled
                    ? 'text-neutral-400 cursor-not-allowed'
                    : 'text-neutral-400 hover:text-neutral-200 hover:bg-neutral-700'
                }`}
            // No aria-label needed
          >
            {pill ? <ArrowUp size={19} strokeWidth={2.6} /> : <Send size={ICON_SIZE} />}
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" sideOffset={5}>
          {sendMessageTooltip}
          {/* Enter sends; the chip only appears when it would. */}
          {sendMessageTooltip === 'Send' ? <TooltipKey>↵</TooltipKey> : null}
        </TooltipContent>
      </Tooltip>
    );
  }, [
    aiIsActive,
    stopRequested,
    onStop,
    stopButtonTooltip,
    onSend,
    sendOrQueueDisabled,
    sendMessageTooltip,
    pill,
    pillBase,
  ]);

  return (
    <div className="flex items-center gap-1">
      {/* Conditional Send or Stop Button */}
      {actionButton}
    </div>
  );
};

const areChatActionButtonPropsEqual = (
  prevProps: ChatActionButtonProps,
  nextProps: ChatActionButtonProps
) => {
  return (
    prevProps.chatState.length === nextProps.chatState.length &&
    // A line SETTLING leaves the length untouched and is exactly what turns the stop button back
    // into a send button, so comparing lengths alone froze the icon on a run that had finished.
    agentIsWorking(prevProps.chatState) === agentIsWorking(nextProps.chatState) &&
    prevProps.isRunningScript === nextProps.isRunningScript &&
    prevProps.isStopRequestedByUI === nextProps.isStopRequestedByUI &&
    prevProps.canSendMessage === nextProps.canSendMessage &&
    prevProps.onSend === nextProps.onSend &&
    prevProps.isQueuingMessage === nextProps.isQueuingMessage &&
    prevProps.pill === nextProps.pill
  );
};

const ChatActionButton = React.memo(ChatActionButtonComponent, areChatActionButtonPropsEqual);

ChatActionButton.displayName = 'ChatActionButton';
export default ChatActionButton;
